import { isEmpty } from 'lodash';
import moment from 'moment-timezone';
import { type MouseEvent, useCallback, useId, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useLocation, useSearchParams } from 'react-router-dom';
import {
  BudgetSpend,
  ContextualMenu,
  Duration,
  PacingStatus,
  Status,
  showNotification,
  TooltipButton,
  useFeatureUser
} from 'sonata-react-components';
import {
  deleteCampaignPlan,
  getCampaignPlanCalendarInfo,
  getCampaignPlanState,
  getEntityDiagnostic,
  syncCampaignPlanState
} from 'sonata-static-storage/lib/entities';

import { PacingStatusModal } from '@/components/common/PacingStatusModal';
import { PromptModal } from '@/components/common/PromptModal';
import { QuickReport } from '@/components/common/QuickReport';
import { normalizeCalendarInfo } from '@/helpers/common/date.helper';
import { createEntityStateLoader } from '@/helpers/entity/entityStateLoader.helper';
import { withPlatformV2OriginMarker } from '@/helpers/navigation/platformOrigin.helper';
import { isSecondaryActionClick } from '@/helpers/navigation/secondaryAction.helper';
import { useModals, useNavigate } from '@/router';
import { getCampaignPlans } from '@/services/campaignPlan.service';
import { useGlobalStore } from '@/store/global';
import { getBaseCurrency } from '@/store/global/baseCurrency/selectors.ts';
import { getUser } from '@/store/global/user/selectors.ts';
import { CampaignsStore, useStore as useCampaignStore } from '@/store/local/campaigns';
import AdsValidationAction from '../../common/Table/AdsValidationAction';
import Subtitle from '../ListComponents/Subtitle/Subtitle';
import { EntityNameTooltip } from '../Shared/EntityNameTooltip';
import { type EntityRowAction, EntityRowActions } from '../Shared/EntityTable/EntityRowActions';
import { EntityTable, type EntityTableColumn } from '../Shared/EntityTable/EntityTable';
import { EntityListStyle } from '../Shared/Shared.styled';
import { useCampaignPlanColumnLabels } from './campaignPlanColumns';
import './campaignPlanList.scss';

import { ChangelogModal } from '@/components/common/ChangelogModal';

import type { CampaignPlanWithExtraData } from 'sonata-static-storage/lib/common/common.types';
import type { CampaignPlan } from 'sonata-static-storage/lib/schemas/multiFlight/campaignPlan.schema';
import type { SelectedMultiflightEntity } from '@/store/local/campaigns';

type CampaignPlanAction = EntityRowAction;

type TaptapListItem = {
  actions: CampaignPlanAction[];
  budgetSpendProps: {
    currencyInfo: ReturnType<typeof getBaseCurrency>;
    entityId: string;
    getBudget: () => Promise<Awaited<ReturnType<typeof getEntityDiagnostic>>>;
    isInDraft: boolean;
  };
  campaignPlan: CampaignPlanWithExtraData;
  durationProps: {
    entityId: string;
    getCalendarInfo: () => ReturnType<typeof getCampaignPlanCalendarInfo>;
    queryKey: string[];
  };
  expandableContent: () => React.ReactNode;
  id: string;
  lastUpdateDate: string;
  lineItemIds: string[];
  pacingStatusProps: {
    entityId: string;
    getPacing: () => Promise<Awaited<ReturnType<typeof getEntityDiagnostic>>>;
    isInDraft: boolean;
    onClick?: () => void;
  };
  statusProps: {
    entityId: string;
    getState: () => ReturnType<typeof getCampaignPlanState>;
    onSyncButtonClick: () => ReturnType<typeof syncCampaignPlanState>;
    queryKey: string[];
  };
  subtitleProps: {
    ioNumbers: number;
    lineItems: number;
    type: 'campaignPlan';
  };
  titleProps: {
    onClick: () => void;
    text: string;
  };
};

type State = {
  changelogConfig?: {
    entityId: string;
    show: boolean;
    title: string;
    userId: string;
  };
  cpToDelete?: CampaignPlan;
  entityPacingClicked?: string;
  showDeletionPrompt: boolean;
  sortParams: {
    by: string;
    order: 'asc' | 'desc';
  };
};

/** Maps each sortable column id to the campaign plan field the backend sorts by. */
const SORT_FIELD_BY_COLUMN: Record<string, string> = {
  lastUpdate: 'updated',
  name: 'name'
};

type Props = {
  cacheSeed: string;
  isColumnVisible: (columnId: string) => boolean;
  filters: Record<string, string | number | (string | number)[]>;
  onListChange: () => void;
  onSelectionChange: (selectedItems: SelectedMultiflightEntity[]) => void;
};

export default function CampaignPlanList({
  cacheSeed,
  filters,
  isColumnVisible: isVisible,
  onListChange,
  onSelectionChange
}: Readonly<Props>) {
  const [{ changelogConfig, cpToDelete, entityPacingClicked, showDeletionPrompt, sortParams }, setState] =
    useState<State>({
      changelogConfig: undefined,
      showDeletionPrompt: false,
      sortParams: {
        by: 'updated',
        order: 'desc'
      }
    });

  const currencyInfo = useGlobalStore(getBaseCurrency);
  const { id: userId = 'N/A' } = useGlobalStore(getUser).entity;
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();
  const location = useLocation();
  const { formatMessage } = useIntl();
  const columnLabels = useCampaignPlanColumnLabels();
  const modals = useModals();
  const listId = useId();
  const { setHasCampaigns } = useCampaignStore() as CampaignsStore;

  const modalsRef = useRef(modals);
  const onListChangeRef = useRef(onListChange);
  const navigateRef = useRef(navigate);
  const locationRef = useRef(location);
  modalsRef.current = modals;
  onListChangeRef.current = onListChange;
  navigateRef.current = navigate;
  locationRef.current = location;

  const showReportingV2 = useFeatureUser('reportingV2');
  const showReportingV2InV2 = useFeatureUser('reportingV2inV2');

  const handleSortChange = useCallback((columnId: string, direction: 'asc' | 'desc') => {
    const by = SORT_FIELD_BY_COLUMN[columnId];
    if (by) {
      setState(prevState => ({ ...prevState, sortParams: { by, order: direction } }));
    }
  }, []);

  const handleTitleClick = (cpId: string) => {
    setSearchParams((prevParams: URLSearchParams) => {
      prevParams.set('tab', 'insertionOrder');
      prevParams.set('cpId', cpId);
      return prevParams;
    });
  };

  const handleDuplicate = () => {
    // Todo: duplicate CP
    onListChangeRef.current();
  };

  const handleDelete = (cp: CampaignPlan) => {
    setState(prevState => ({
      ...prevState,
      cpToDelete: cp,
      showDeletionPrompt: true
    }));
  };

  const handleConfirmDeletion = async () => {
    try {
      await deleteCampaignPlan(userId, cpToDelete!._id!);
      const { totalItems } = await getCampaignPlans(userId, new URLSearchParams({ itemsPerPage: '1' }));
      if (totalItems === 0) {
        setHasCampaigns(false);
      }
      onListChange();
      setState(prevState => ({
        ...prevState,
        cpToDelete: undefined,
        showDeletionPrompt: false
      }));
      showNotification(
        'success',
        formatMessage({
          defaultMessage: 'Campaign Plan deleted successfully',
          id: 'campaignPlans.notifications.deleted'
        })
      );
    } catch {
      showNotification(
        'error',
        formatMessage({
          defaultMessage: 'Failure deleting the Campaign Plan',
          id: 'campaignPlans.notifications.notDeleted'
        })
      );
    }
  };

  const handleCancelDeletion = () => {
    setState(prevState => ({
      ...prevState,
      cpToDelete: undefined,
      showDeletionPrompt: false
    }));
  };

  const handleCloseModal = () => {
    setState(prevState => ({
      ...prevState,
      entityPacingClicked: undefined
    }));
  };

  const handleChangelogModalClose = () => {
    setState(prevState => ({
      ...prevState,
      changelogConfig: undefined
    }));
  };

  const handleSelectionChange = (items: TaptapListItem[]) => {
    const selectedCps: SelectedMultiflightEntity[] = items.map(item => ({
      id: item.id,
      lineItemIds: item.lineItemIds
    }));
    onSelectionChange(selectedCps);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: cacheSeed is intentionally listed to trigger re-fetches on external invalidation
  const fetchData = useCallback(
    async (params: { page: number; pageSize: number }) => {
      const filterParams = Object.fromEntries(
        Object.entries(filters).map(([k, v]) => [k, Array.isArray(v) ? v.join(',') : String(v)])
      );
      const searchParams = new URLSearchParams({
        includeExtraData: 'true',
        itemsPerPage: params.pageSize.toString(),
        page: params.page.toString(),
        sortBy: sortParams.by,
        sortOrder: sortParams.order,
        ...filterParams
      });

      try {
        const response = await getCampaignPlans(userId, searchParams);
        // Each list load gets its own state cache, so rows show the state their loaders just fetched
        const loadedAt = Date.now().toString();

        const items: TaptapListItem[] =
          response?.items?.map(cp => {
            const entityDiagnosticPromise = getEntityDiagnostic(userId, cp._id!, 'campaignPlan');
            const entityState = createEntityStateLoader(() => getCampaignPlanState(userId, cp._id!));
            const getCalendarInfo = () => getCampaignPlanCalendarInfo(userId, cp._id!).then(normalizeCalendarInfo);
            const liIds = cp.extraData.lineItemIds.join();
            let reportingHref = '';
            if (showReportingV2InV2) {
              reportingHref = `/reporting?cids=${liIds}`;
            } else if (showReportingV2) {
              reportingHref = withPlatformV2OriginMarker(
                `${taptap.config.sonataUrl}app.html#!/reporting-v2&q=${liIds}`
              );
            } else {
              reportingHref = withPlatformV2OriginMarker(`${taptap.config.sonataUrl}app.html#!/reporting/${liIds}`);
            }

            const handleReportingClick = (ev: MouseEvent) => {
              if (isSecondaryActionClick(ev)) {
                window.open(reportingHref, '_blank', 'noopener,noreferrer');
              } else {
                window.location.assign(reportingHref);
              }
            };

            const actions: CampaignPlanAction[] = [];

            if (!isEmpty(cp.extraData.lineItemIds)) {
              actions.push({
                content: (
                  <span
                    onContextMenu={ev => {
                      ev.preventDefault();
                      handleReportingClick(ev);
                    }}
                  >
                    <TooltipButton
                      buttonConfig={{
                        buttonOnClick: handleReportingClick,
                        buttonShape: 'link',
                        buttonSize: 'ultraSmall',
                        buttonType: 'primary'
                      }}
                      icon="fa-solid fa-chart-mixed"
                      id={`cp-reporting-${cp._id?.toString()}`}
                      isButton
                      tooltipConfig={{
                        tooltipEvent: 'mouseenter',
                        tooltipPlace: 'top'
                      }}
                    >
                      {formatMessage({
                        defaultMessage: 'View Campaign Report',
                        id: 'campaignPlans.reporting.tooltip'
                      })}
                    </TooltipButton>
                  </span>
                ),
                id: `cp-reporting-${cp._id?.toString()}`,
                isCustomAction: true
              });
            }

            actions.push(
              {
                content: (
                  <div className="adStatusContainer">
                    <AdsValidationAction
                      className="campaignPlanList__tableListActionsAdsButton"
                      entityId={cp._id!}
                      fetchState={() => entityState.load()}
                      id={`cp-adsStatus-${cp._id?.toString()}`}
                      onClick={() =>
                        modalsRef.current.open('/campaigns/AdsValidation', {
                          at: {
                            pathname: '/campaigns',
                            search: location.search
                          },
                          state: {
                            modalId: 'adsValidation',
                            origin: 'campaignPlan',
                            value: cp
                          }
                        })
                      }
                      queryKey={[loadedAt]}
                    />
                  </div>
                ),
                id: `cp-adsStatusContainer-${cp._id?.toString()}`,
                isCustomAction: true
              },
              {
                icon: 'fa fa-pen-to-square',
                iconClassName: '',
                id: `cp-edit-${cp._id}`,
                onClick: () => {
                  modalsRef.current.open('/campaigns/new/campaignPlanSettingsModal', {
                    at: {
                      pathname: '/campaigns',
                      search: location.search
                    },
                    state: {
                      campaignPlan: cp,
                      modalId: 'campaignPlanSettings-modal'
                    }
                  });
                }
              },
              {
                content: (
                  <ContextualMenu
                    buttons={[
                      [
                        {
                          icon: 'fa fa-list-alt',
                          id: `cp-action-changelog-${cp._id?.toString()}`,
                          onClick: () => {
                            setState(prevState => ({
                              ...prevState,
                              changelogConfig: {
                                entityId: cp._id!,
                                show: true,
                                title: cp.name,
                                userId: cp.user
                              }
                            }));
                          },
                          text: formatMessage({
                            defaultMessage: 'Changelog',
                            id: 'campaignPlans.list.buttons.changeLog'
                          })
                        }
                      ],
                      [
                        {
                          disabled: true,
                          icon: 'fa fa-files-o',
                          id: `cp-action-duplicate-${cp._id}`,
                          onClick: () => handleDuplicate(),
                          text: formatMessage({
                            defaultMessage: 'Duplicate',
                            id: 'campaignPlans.list.buttons.duplicate'
                          })
                        },
                        {
                          disabled: !!cp.extraData.insertionOrderIds.length || !!cp.extraData.lineItemIds.length,
                          icon: 'fa fa-trash-alt',
                          id: `cp-action-delete-${cp._id}`,
                          onClick: () => handleDelete(cp),
                          text: formatMessage({
                            defaultMessage: 'Delete',
                            id: 'common.delete'
                          })
                        },
                        {
                          icon: 'fal fa-plus',
                          id: `cp-action-createIO-${cp._id}`,
                          onClick: () =>
                            modalsRef.current.open('/campaigns/new/insertionOrderModal', {
                              at: {
                                pathname: '/campaigns',
                                search: locationRef.current.search
                              },
                              state: {
                                campaignPlan: cp,
                                modalId: 'insertionOrder-modal'
                              }
                            }),
                          text: formatMessage({
                            defaultMessage: 'Add Insertion Order',
                            id: 'campaignPlans.list.buttons.createIO'
                          })
                        }
                      ]
                    ]}
                    direction="vertical"
                    id={`cp-actions-${cp._id}`}
                  />
                ),
                id: `cp-more-actions-${cp._id}`,
                isCustomAction: true
              }
            );

            return {
              actions,
              budgetSpendProps: {
                currencyInfo,
                entityId: cp._id!,
                getBudget: () => entityDiagnosticPromise,
                isInDraft: !cp.completed
              },
              campaignPlan: cp,
              durationProps: {
                entityId: cp._id!,
                getCalendarInfo,
                queryKey: [cp._id!]
              },
              expandableContent: () => (
                <QuickReport entity={cp} entityType="campaignPlan" id={`multiflightExpandable-${cp._id}`} />
              ),
              id: cp._id!,
              lastUpdateDate: moment.utc(cp.updated || cp.created).format('DD/MM/YYYY'),
              lineItemIds: cp.extraData.lineItemIds || [],
              pacingStatusProps: {
                entityId: cp._id!,
                getPacing: () => entityDiagnosticPromise,
                isInDraft: !cp.completed,
                onClick: cp.completed
                  ? () => {
                      setState(prevState => ({
                        ...prevState,
                        entityPacingClicked: cp._id
                      }));
                    }
                  : undefined
              },
              statusProps: {
                entityId: cp._id!,
                getState: () => entityState.load(),
                onSyncButtonClick: async () => {
                  try {
                    const result = await syncCampaignPlanState(userId, cp._id!);
                    entityState.invalidate();
                    return result;
                  } catch {
                    showNotification(
                      'error',
                      formatMessage({
                        defaultMessage: 'Campaign Plan failed to be synced, please try again',
                        id: 'campaignPlan.list.syncCampaignPlan.error'
                      })
                    );
                    return undefined;
                  }
                },
                queryKey: [loadedAt]
              },
              subtitleProps: {
                ioNumbers: cp.extraData.insertionOrderIds?.length ?? 0,
                lineItems: cp.extraData.lineItemIds?.length ?? 0,
                type: 'campaignPlan' as const
              },
              titleProps: {
                onClick: () => handleTitleClick(cp._id!),
                text: cp.name
              }
            };
          }) ?? [];

        return {
          data: items,
          page: params.page,
          pageSize: params.pageSize,
          total: response.totalItems,
          totalPages: Math.ceil(response.totalItems / params.pageSize)
        };
      } catch {
        showNotification(
          'error',
          formatMessage({
            defaultMessage: 'Failed to load campaign plans',
            id: 'campaignPlans.fetchData.error'
          })
        );
        return { data: [], page: params.page, pageSize: params.pageSize, total: 0, totalPages: 0 };
      }
    },
    [userId, filters, cacheSeed, sortParams, currencyInfo, formatMessage]
  );

  const columns: EntityTableColumn<TaptapListItem>[] = [
    {
      header: columnLabels.status,
      id: 'status',
      render: item => (
        <div className="multiflightStatus campaignPlanList__statusCell">
          <Status
            entityId={item.statusProps.entityId}
            getState={item.statusProps.getState}
            onSyncButtonClick={item.statusProps.onSyncButtonClick}
            queryKey={item.statusProps.queryKey}
          />
        </div>
      ),
      width: 'var(--campaignPlanList-col-status)'
    },
    {
      header: columnLabels.duration,
      id: 'duration',
      render: item => (
        <div className="multiflightDuration">
          <Duration
            entityId={item.durationProps.entityId}
            getCalendarInfo={item.durationProps.getCalendarInfo}
            queryKey={item.durationProps.queryKey}
          />
        </div>
      ),
      width: 'var(--campaignPlanList-col-duration)'
    },
    {
      header: columnLabels.lastUpdate,
      id: 'lastUpdate',
      render: item => <span className="multiflightLastUpdate">{item.lastUpdateDate}</span>,
      sortable: true,
      width: 'var(--campaignPlanList-col-last-update)'
    },
    {
      align: 'right',
      header: columnLabels.budgetSpend,
      id: 'budgetSpend',
      render: item => (
        <span className="multiflightBudgetSpend">
          <BudgetSpend
            currencyInfo={currencyInfo}
            entityId={item.budgetSpendProps.entityId}
            getBudget={item.budgetSpendProps.getBudget}
            isInDraft={item.budgetSpendProps.isInDraft}
          />
        </span>
      ),
      width: 'var(--campaignPlanList-col-budget)'
    }
  ];
  const visibleColumns = columns.filter(column => isVisible(column.id));

  return (
    <EntityListStyle className="campaignPlanList">
      {showDeletionPrompt && (
        <PromptModal
          cancelCallback={handleCancelDeletion}
          confirmCallback={handleConfirmDeletion}
          texts={{
            buttons: {
              cancel: formatMessage({
                defaultMessage: 'Cancel',
                id: 'common.cancel'
              }),
              confirm: formatMessage({
                defaultMessage: 'Confirm',
                id: 'common.confirm'
              })
            },
            description: formatMessage(
              {
                defaultMessage:
                  "Deleting the campaign {campaignPlanName} can't be undone and all associated information will be permanently lost.",
                id: 'campaignPlans.prompts.deletion.description'
              },
              {
                campaignPlanName: cpToDelete?.name
              }
            ),
            title: formatMessage({
              defaultMessage: 'Are you sure you want to delete the selected campaign?',
              id: 'campaignPlans.prompts.deletion.title'
            })
          }}
          variant="warning"
        />
      )}
      <EntityTable<TaptapListItem>
        actionsWidth="var(--campaignPlanList-col-actions)"
        className="campaignPlanList"
        columns={visibleColumns}
        emptyStateTitle={formatMessage({
          defaultMessage: 'There is no data for the current search.',
          id: 'campaignPlans.list.emptyState.searchTitle'
        })}
        expandOnRowClick
        fetchData={fetchData}
        id={listId}
        initialPageSize={15}
        nameColumn={{
          header: formatMessage({ defaultMessage: 'Campaign Plan', id: 'campaignPlans.list.toolbar.name' }),
          render: item => (
            <div className="multiflightTitle">
              <EntityNameTooltip name={item.titleProps.text}>
                <button
                  className="campaignPlanList__titleButton"
                  data-row-action
                  onClick={item.titleProps.onClick}
                  type="button"
                >
                  {item.titleProps.text}
                </button>
              </EntityNameTooltip>
              <Subtitle
                ioNumbers={item.subtitleProps.ioNumbers}
                lineItems={item.subtitleProps.lineItems}
                type={item.subtitleProps.type}
              />
            </div>
          ),
          sortable: true
        }}
        onSelectionChange={handleSelectionChange}
        onSortChange={handleSortChange}
        renderActions={item => <EntityRowActions actions={item.actions} className="campaignPlanList" />}
        renderLeading={item => (
          <PacingStatus
            entityId={item.pacingStatusProps.entityId}
            getPacing={item.pacingStatusProps.getPacing}
            isInDraft={item.pacingStatusProps.isInDraft}
            onClick={item.pacingStatusProps.onClick}
          />
        )}
      />
      {!!entityPacingClicked && (
        <PacingStatusModal
          currencyInfo={currencyInfo}
          entityId={entityPacingClicked}
          entityType="campaignPlan"
          isOpen={!!entityPacingClicked}
          onClose={handleCloseModal}
          userId={userId}
        />
      )}
      {changelogConfig?.show && (
        <ChangelogModal
          entity="campaignPlan"
          entityId={changelogConfig.entityId}
          onCloseModalCallback={handleChangelogModalClose}
          title={changelogConfig.title}
          userId={changelogConfig.userId}
        />
      )}
    </EntityListStyle>
  );
}
